import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InstagramFeed from '../components/InstagramFeed';
import CartIcon from '../components/CartIcon';
import WelcomeModal from '../components/WelcomeModal';
import PWAInstallBanner from '../components/PWAInstallBanner';
import { ArrowRight, Truck, RotateCcw, ShieldCheck, Sparkles } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  // Video de fondo - JRF Sportswear
  const heroVideoUrl = '/hero.mp4';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const sections = [
    {
      id: 'mujer',
      title: 'Women',
      subtitle: 'Lotus Collection',
      image: `${BACKEND_URL}/api/uploads/garments/59819dfe9564495fb2cbdf9aa3f77a1d.jpg?w=800`,
      path: '/mujer',
    },
    {
      id: 'hombre',
      title: 'Men',
      subtitle: 'Lotus Collection',
      image: `${BACKEND_URL}/api/uploads/garments/110e2c7fa8b84fc293ab3399e5d17ae3.jpg?w=800`,
      path: '/hombre',
    },
    {
      id: 'accesorios',
      title: 'Accessories',
      subtitle: 'Complete your style',
      image: `${BACKEND_URL}/api/uploads/garments/8e2b39762d644b9caa6b47ee48dd3b06.jpg?w=800`,
      path: '/accesorios',
    },
  ];

  return (
    <div className="min-h-screen bg-white text-black" data-testid="landing-page">
      <Header />

      {/* Hero with Video */}
      <header className="relative w-full overflow-hidden bg-black" style={{ height: 'calc(100vh - 100px)', minHeight: '600px' }}>
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideoUrl} type="video/mp4" />
        </video>

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/30" />

        {/* Hero content */}
        <div className="relative h-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 sm:pb-24">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-white/90 mb-4">
            Nueva Colección Tulips · Primavera 2026
          </p>
          <h1
            className="text-white font-black uppercase leading-[0.9] tracking-tighter text-5xl sm:text-7xl lg:text-8xl mb-6"
            data-testid="hero-title"
          >
            Confianza.
            <br />
            Estilo. Fuerza.
          </h1>
          <p className="text-white/80 max-w-md text-sm sm:text-base leading-relaxed mb-8">
            Donde el estilo se convierte en poder. Ropa deportiva diseñada para superar tus límites.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/mujer')}
              className="bg-white text-black hover:bg-neutral-200 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2 group"
              data-testid="hero-cta-women"
            >
              Shop Women
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
            </button>
            <button
              onClick={() => navigate('/hombre')}
              className="bg-transparent border border-white text-white hover:bg-white hover:text-black px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2"
              data-testid="hero-cta-men"
            >
              Shop Men
            </button>
          </div>
        </div>
      </header>

      {/* Category Grid (Gymshark-style, 3 cols, no gaps) */}
      <section className="w-full" data-testid="category-grid">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => navigate(section.path)}
              data-testid={`section-${section.id}`}
              className="group relative overflow-hidden bg-neutral-100 text-left"
              style={{ aspectRatio: '3/4' }}
            >
              <img
                src={section.image}
                alt={section.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-105"
              />
              {/* Bottom gradient for text legibility */}
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />

              {/* Content bottom-left */}
              <div className="absolute bottom-0 left-0 p-6 sm:p-8">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/80 mb-2">
                  {section.subtitle}
                </p>
                <h2 className="text-white font-black uppercase tracking-tighter text-4xl sm:text-5xl mb-4">
                  {section.title}
                </h2>
                <span className="inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-[0.2em] border-b border-white pb-1 group-hover:gap-3 transition-all">
                  Shop now
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Editorial Banner */}
      <section className="bg-black text-white py-20 sm:py-28" data-testid="editorial-banner">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-white/60 mb-6">
            Train hard · Feel unstoppable
          </p>
          <h2 className="font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-8xl leading-[0.9] mb-8">
            Discover
            <br />
            our collections
          </h2>
          <button
            onClick={() => navigate('/mujer')}
            className="mt-4 bg-white text-black hover:bg-neutral-200 px-10 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-2 group"
            data-testid="editorial-cta-catalog"
          >
            Ver colecciones
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
          </button>
        </div>
      </section>

      {/* Collections Showcase — LOTUS + TULIPS */}
      <section className="py-20 sm:py-24 bg-white" data-testid="collections-showcase">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 sm:mb-16">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Elige tu esencia
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-none">
              Our collections
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {/* LOTUS */}
            <button
              onClick={() => navigate('/mujer?collection=LOTUS')}
              className="group relative overflow-hidden bg-neutral-100 text-left aspect-[4/5]"
              data-testid="collection-card-lotus"
            >
              <img
                src={`${BACKEND_URL}/api/uploads/garments/e3abf69081b745a898a2e5bf848173b3.jpg?w=1200`}
                alt="Lotus Collection"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-7 xl:p-8 max-w-md">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/80 mb-3">
                  Colección 01
                </p>
                <h3 className="text-white font-black uppercase tracking-tighter text-5xl lg:text-4xl xl:text-5xl mb-4 leading-none">
                  Lotus
                </h3>
                <p className="text-white/90 text-sm mb-6 leading-relaxed max-w-xs">
                  Transformación y resiliencia. Prendas que florecen en cada movimiento.
                </p>
                <span className="inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-[0.2em] border-b border-white pb-1 group-hover:gap-3 transition-all">
                  Explorar Lotus
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
              </div>
            </button>

            {/* TULIPS */}
            <button
              onClick={() => navigate('/mujer?collection=TULIPS')}
              className="group relative overflow-hidden bg-neutral-100 text-left aspect-[4/5]"
              data-testid="collection-card-tulips"
            >
              <img
                src="https://customer-assets-0z36b82j.emergentagent.net/job_51102157-b860-4065-bd63-18fbb8661d8b/artifacts/2ldml279_205019.webp"
                alt="Tulips Collection"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-7 xl:p-8 max-w-md">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/90 mb-3">
                  Colección 02
                </p>
                <h3 className="text-white font-black uppercase tracking-tighter text-5xl lg:text-4xl xl:text-5xl mb-4 leading-none">
                  Tulips
                </h3>
                <p className="text-white/90 text-sm mb-6 leading-relaxed max-w-xs">
                  Delicadeza que se convierte en fuerza. Prendas que celebran tu feminidad.
                </p>
                <span className="inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-[0.2em] border-b border-white pb-1 group-hover:gap-3 transition-all">
                  Explorar Tulips
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
              </div>
            </button>

            {/* COSMOS — new */}
            <button
              onClick={() => navigate('/mujer?collection=COSMOS')}
              className="group relative overflow-hidden bg-neutral-100 text-left aspect-[4/5]"
              data-testid="collection-card-cosmos"
            >
              <img
                src={`${BACKEND_URL}/api/uploads/garments/903a7c43e1114828b6296d0c801d6474.jpg?w=1200`}
                alt="Cosmos Collection"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute top-4 right-4 bg-white text-black text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1" data-testid="cosmos-new-badge">
                New
              </div>
              <div className="absolute bottom-0 left-0 p-7 xl:p-8 max-w-md">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/90 mb-3">
                  Colección 03 · Nueva
                </p>
                <h3 className="text-white font-black uppercase tracking-tighter text-5xl lg:text-4xl xl:text-5xl mb-4 leading-none">
                  Cosmos
                </h3>
                <p className="text-white/90 text-sm mb-6 leading-relaxed max-w-xs">
                  Tu esencia es única: deja que brille. La nueva colección ya está aquí.
                </p>
                <span className="inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-[0.2em] border-b border-white pb-1 group-hover:gap-3 transition-all">
                  Explorar Cosmos
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
              </div>
            </button>

            {/* IKIGAI — coming soon */}
            <button
              onClick={() => navigate('/ikigai')}
              className="group relative overflow-hidden bg-[#F5E8DF] text-left aspect-[4/5]"
              data-testid="collection-card-ikigai"
            >
              <img
                src="/ikigai-card.webp"
                alt="Ikigai Collection"
                className="absolute top-0 left-0 w-full h-auto transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute top-4 right-4 bg-[#8B2B3A] text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1" data-testid="ikigai-card-badge">
                Próximamente
              </div>
              <div className="absolute bottom-0 left-0 p-7 xl:p-8 max-w-md text-[#8B2B3A]">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-80 mb-3">
                  Colección 04 · 生き甲斐
                </p>
                <h3 className="font-black uppercase tracking-tighter text-5xl lg:text-4xl xl:text-5xl mb-4 leading-none">
                  Ikigai
                </h3>
                <p className="text-neutral-700 text-sm mb-6 leading-relaxed max-w-xs">
                  Entrena con propósito. Tu razón de ser, en cada movimiento.
                </p>
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] border-b border-[#8B2B3A] pb-1 group-hover:gap-3 transition-all">
                  Descubrir Ikigai
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Features Section - Minimal */}
      <section className="py-16 sm:py-20 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
            <div className="flex flex-col items-center text-center px-2">
              <Truck className="w-6 h-6 mb-3" strokeWidth={1.5} />
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5">
                Envío gratis
              </h3>
              <p className="text-xs text-neutral-500">Desde 80€</p>
            </div>
            <div className="flex flex-col items-center text-center px-2">
              <Sparkles className="w-6 h-6 mb-3" strokeWidth={1.5} />
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5">
                Calidad premium
              </h3>
              <p className="text-xs text-neutral-500">Materiales técnicos</p>
            </div>
            <div className="flex flex-col items-center text-center px-2">
              <RotateCcw className="w-6 h-6 mb-3" strokeWidth={1.5} />
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5">
                Devolución fácil
              </h3>
              <p className="text-xs text-neutral-500">15 días</p>
            </div>
            <div className="flex flex-col items-center text-center px-2">
              <ShieldCheck className="w-6 h-6 mb-3" strokeWidth={1.5} />
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5">
                Pago seguro
              </h3>
              <p className="text-xs text-neutral-500">Stripe · PayPal</p>
            </div>
          </div>
        </div>
      </section>

      {/* Loyalty CTA Strip */}
      <section
        onClick={() => navigate('/fidelidad')}
        className="cursor-pointer group bg-white hover:bg-neutral-50 transition-colors border-b border-neutral-200"
        data-testid="section-fidelidad"
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-1">
              Programa
            </p>
            <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight">
              Fidelidad JRF · 100 pts = 5€
            </h3>
          </div>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] border-b border-black pb-1 group-hover:gap-3 transition-all">
            Ver puntos
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        </div>
      </section>

      {/* Size Guide CTA */}
      <section
        onClick={() => navigate('/guia-tallas')}
        className="cursor-pointer group bg-white hover:bg-neutral-50 transition-colors border-b border-neutral-200"
        data-testid="section-guia-tallas"
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-1">
              Ayuda
            </p>
            <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight">
              Guía de tallas
            </h3>
          </div>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] border-b border-black pb-1 group-hover:gap-3 transition-all">
            Ver guía
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        </div>
      </section>

      {/* About */}
      <section className="py-20 sm:py-24 bg-white" data-testid="section-about-us">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_51102157-b860-4065-bd63-18fbb8661d8b/artifacts/xassaqji__MG_1377.jpg"
                alt="Jeilyn Rodríguez Furcal - Fundadora de JRF Sportswear"
                className="w-full h-[560px] object-cover"
                style={{ objectPosition: 'center 15%' }}
              />
            </div>

            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-4">
                Nuestra historia
              </p>
              <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-none mb-8">
                JRF
                <br />
                Sportswear
              </h2>
              <div className="space-y-5 text-neutral-700 leading-relaxed text-base">
                <p>
                  JRF Sportswear nace de una historia personal de esfuerzo, disciplina y sueños. La marca fue creada por{' '}
                  <strong className="text-black">Jeilyn Rodríguez Furcal</strong>, apasionada por el deporte, el bienestar y el crecimiento personal.
                </p>
                <p>
                  Cada colección está diseñada pensando en personas que desean sentirse{' '}
                  <strong className="text-black">cómodas, seguras y fuertes</strong>, dentro y fuera del entrenamiento. Prendas que combinan funcionalidad, estilo y significado.
                </p>
                <p>
                  La primera colección, <strong className="text-black">Lotus</strong>, está inspirada en la flor de loto, símbolo de transformación y resiliencia. Así como esta flor florece incluso en aguas turbias, JRF Sportswear recuerda que los desafíos también pueden ser el comienzo de algo hermoso.
                </p>
                <p className="text-black font-medium border-l-2 border-black pl-5 uppercase text-sm tracking-wider">
                  Una comunidad que cree en el poder de la disciplina, el bienestar y los sueños.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Feed */}
      <InstagramFeed />

      <Footer />

      {/* Welcome Modal (first visit) */}
      <WelcomeModal />

      {/* PWA Install Banner (aparece si el navegador soporta instalación) */}
      <PWAInstallBanner />
    </div>
  );
};

export default LandingPage;
